/**
 * 전체 소모품 페이지 — 회사 창고 재고와 현장에 나가 있는 수량을 한 화면에서 맞춰 본다.
 *
 * 세 덩어리다.
 *   1) 품목별 현황 — 회사 재고 / 현장 사용중 / 사용 현장 수를 품명으로 묶은 표.
 *   2) 회사 전체 소모품 — 품명·재고 수량·비고.
 *   3) 현장별 소모품 — 품명·사용중·비고. 현장 자재 관리 페이지와 같은 내용이다.
 *
 * 메모는 목록 API 로 한 번에 받아 카드에 내려준다 — 카드마다 조회하면 현장 수만큼
 * 요청이 나가고, 무엇보다 (1) 의 합계가 카드 저장과 어긋난다.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Inventory2Outlined as ConsumableIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

import ConsumableNoteCard from '../../components/ConsumableNoteCard';
import UserProfileMenu from '../../components/UserProfileMenu';
import {
  SiteConsumableNote,
  SiteConsumableNoteService,
  COMPANY_NOTE_ID,
} from '../../services/SiteConsumableNoteService';
import {
  buildConsumableSummary,
  parseConsumableSheet,
} from '../../services/consumableSheet';
import { SiteProjectService } from '../../services/SiteProjectService';
import { SiteProject } from '../../models/SiteProject';

const emptyNote = (projectId: number): SiteConsumableNote => ({
  id: 0,
  site_project_id: projectId,
  content: '',
});

const siteLabel = (s: SiteProject): string => s.name_ko?.trim() || s.name;

const ConsumablesPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [sites, setSites] = useState<SiteProject[] | null>(null);
  const [notes, setNotes] = useState<Record<number, SiteConsumableNote>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([SiteProjectService.listProjects(), SiteConsumableNoteService.listAll()])
      .then(([list, allNotes]) => {
        if (cancelled) return;
        setSites([...list].sort((a, b) => siteLabel(a).localeCompare(siteLabel(b), 'ko')));
        const byProject: Record<number, SiteConsumableNote> = {};
        allNotes.forEach((n) => {
          byProject[n.site_project_id] = n;
        });
        setNotes(byProject);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '소모품 조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const noteOf = useCallback(
    (projectId: number): SiteConsumableNote => notes[projectId] ?? emptyNote(projectId),
    [notes],
  );

  const onSaved = useCallback((saved: SiteConsumableNote) => {
    setNotes((prev) => ({ ...prev, [saved.site_project_id]: saved }));
  }, []);

  const companyItems = useMemo(
    () => parseConsumableSheet(noteOf(COMPANY_NOTE_ID).content).items,
    [noteOf],
  );

  const siteItems = useMemo(
    () => (sites ?? []).map((s) => parseConsumableSheet(noteOf(s.id).content).items),
    [sites, noteOf],
  );

  const summary = useMemo(
    () => buildConsumableSummary(companyItems, siteItems),
    [companyItems, siteItems],
  );

  /** 현장 표의 품명이 회사 표와 갈리면 합계가 쪼개진다 — 이미 쓰인 이름을 전부 추천한다. */
  const nameOptions = useMemo(() => summary.map((r) => r.name), [summary]);

  const q = query.trim().toLowerCase();
  const matchesItem = (name: string) => name.toLowerCase().includes(q);
  const visibleSummary = q ? summary.filter((r) => matchesItem(r.name)) : summary;
  const visibleSites = (sites ?? []).filter((s, i) => {
    if (!q) return true;
    return siteLabel(s).toLowerCase().includes(q) || siteItems[i]?.some((r) => matchesItem(r.name));
  });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: { xs: 1.5, sm: 4 },
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          position: 'sticky',
          top: 0,
          zIndex: 3,
        }}
      >
        <IconButton onClick={() => navigate('/projects')} size="small" aria-label="뒤로가기">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <ConsumableIcon fontSize="small" color="action" />
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
          전체 소모품
        </Typography>
        <UserProfileMenu />
      </Paper>

      <Container maxWidth="lg" sx={{ py: 2, pb: 12 }}>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            size="small"
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="품명 또는 현장 이름으로 찾기"
            inputProps={{ 'aria-label': '소모품 검색' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!loading && (
            <>
              {/* 1) 품목별 현황 — 이 페이지를 여는 이유. 창고에 남은 수량과 현장에
                  나가 있는 수량을 품명으로 맞춰 본다. */}
              <Paper variant="outlined" sx={{ p: { xs: 1.75, sm: 2.5 }, borderRadius: 2 }} data-testid="consumable-summary">
                <Typography variant="subtitle1" fontWeight={700}>
                  품목별 현황
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  회사 재고와 현장에서 쓰는 중인 수량을 품명으로 묶어 보여 줍니다.
                </Typography>

                {visibleSummary.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {q ? '검색 결과가 없습니다.' : '아직 기입된 소모품이 없습니다.'}
                  </Typography>
                ) : isMobile ? (
                  <Stack divider={<Divider flexItem />}>
                    {visibleSummary.map((r) => (
                      <Box key={r.name} sx={{ py: 1 }}>
                        <Typography variant="body2" fontWeight={700}>
                          {r.name}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }} useFlexGap>
                          <Chip size="small" variant="outlined" label={`회사 재고 ${r.stock}`} />
                          <Chip size="small" variant="outlined" color="primary" label={`현장 사용중 ${r.inUse}`} />
                          <Chip size="small" variant="outlined" label={`${r.siteCount}개 현장`} />
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>품명</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, width: 120 }}>
                          회사 재고
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, width: 120 }}>
                          현장 사용중
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, width: 120 }}>
                          사용 현장
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleSummary.map((r) => (
                        <TableRow key={r.name} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {r.stock}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {r.inUse}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary' }}>
                            {r.siteCount}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Paper>

              {/* 2) 회사 창고. */}
              <ConsumableNoteCard
                projectId={COMPANY_NOTE_ID}
                title="회사 전체 소모품"
                subtitle="회사에서 보유·관리하는 소모품의 재고 수량입니다."
                quantityLabel="재고 수량"
                nameOptions={nameOptions}
                note={noteOf(COMPANY_NOTE_ID)}
                onSaved={onSaved}
              />

              {/* 3) 현장별. 카드가 여럿이라 접어 두고, 칩으로 품목 수·합계만 보인다. */}
              <Typography variant="overline" color="text.secondary" sx={{ pt: 1 }}>
                현장별 소모품
              </Typography>
              {sites && sites.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  등록된 현장이 없습니다.
                </Typography>
              )}
              {sites && sites.length > 0 && visibleSites.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  검색 결과가 없습니다.
                </Typography>
              )}
              {visibleSites.map((s) => (
                <ConsumableNoteCard
                  key={s.id}
                  projectId={s.id}
                  title={siteLabel(s)}
                  quantityLabel="사용중"
                  nameOptions={nameOptions}
                  note={noteOf(s.id)}
                  onSaved={onSaved}
                  defaultCollapsed
                />
              ))}
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default ConsumablesPage;
